import { useEffect, useRef, useState } from "react";
import { getPremiumStatus } from "@/lib/premiumApi";

/**
 * AdSlot — renders a Google AdSense unit with a graceful editorial placeholder
 * behind it. While AdSense is awaiting approval or has no ad to serve, the
 * placeholder remains visible so the layout never feels broken.
 * Premium members see no ads at all.
 */
export const AdSlot = ({
  slot,
  format = "auto",
  variant = "banner",
  label = "AD",
  testId,
}) => {
  const client = process.env.REACT_APP_ADSENSE_CLIENT;
  const insRef = useRef(null);
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    getPremiumStatus().then((s) => setIsPremium(!!s.premium));
  }, []);

  useEffect(() => {
    if (!client || !slot || isPremium) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      /* adsbygoogle not loaded yet — silent */
    }
  }, [client, slot, isPremium]);

  // Premium = zero ads, zero placeholder.
  if (isPremium) return null;

  const heights = {
    banner: "h-[120px] md:h-[120px]",
    leaderboard: "h-[90px] md:h-[120px]",
    rectangle: "h-[250px] md:h-[280px]",
  };

  // When no AdSense client/slot is configured, render the plain placeholder.
  if (!client || !slot) {
    return (
      <div data-testid={testId || "ad-slot-placeholder"} className="border-y border-black bg-[#f4f4f5]">
        <div className={`mx-auto max-w-[1400px] px-6 lg:px-10 ${heights[variant]} flex items-center justify-between`}>
          <span className="overline text-[#52525b]">{label} · Your ad here</span>
          <span className="font-mono text-xs text-[#52525b] hidden md:block">
            Set REACT_APP_ADSENSE_CLIENT to activate
          </span>
        </div>
      </div>
    );
  }

  // Live AdSense slot — render <ins> with an editorial placeholder in the background.
  // The placeholder remains visible until AdSense approves the site & serves an ad,
  // at which point Google paints the <ins> element over it.
  return (
    <div data-testid={testId || "ad-slot-live"} className="border-y border-black bg-[#f4f4f5] relative">
      <div className={`mx-auto max-w-[1400px] px-6 lg:px-10 ${heights[variant]} flex items-center justify-between`}>
        <span className="overline text-[#52525b]">{label}</span>
        <span className="font-mono text-xs text-[#52525b] hidden md:block">
          Awaiting AdSense · placeholder shown until approved
        </span>
      </div>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{
          display: "block",
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
        }}
        data-ad-client={client}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
};

export default AdSlot;
